"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useTransition } from "react"
import SuggestedSearchPills from "../SuggestedSearchPills"
import SearchBar from "../SearchBar"
import SearchFilters from "../SearchFilters"
import SearchTypeToggle from "../SearchTypeToggle"
import { useRouter } from "next/navigation"
import Loader from "../../../../components/common/Loader"
import { buildSearchHref } from "../../../../lib/search/buildSearchHref"
import {
  SEARCH_TYPE_GROUPS,
  SEARCH_TYPE_ORGANIZATIONS,
  SEARCH_TYPE_PROVIDERS,
  parseSearchType,
  type SearchType,
} from "../../../../lib/search/searchParams"
import { SEARCH_FILTERS, SEARCH_FILTERS_BY_TYPE } from "../../../../lib/constants"

type UpdateSearchOptions = Partial<{
  newSearchTerm: string
  newLocation: string
  newGroupTypes: string[]
  newProviderTypes: string[]
  newVirtual: boolean | null
  newInPerson: boolean | null
  newLatitude: number | null | undefined
  newLongitude: number | null | undefined
  newType: SearchType
}>

const HEADER_COPY: Record<SearchType, { title: [string, string]; blurb: string }> = {
  [SEARCH_TYPE_GROUPS]: {
    title: ["Discover", "Mental Health Groups"],
    blurb:
      "Search for what mental health topic you want to explore in a group with others.",
  },
  [SEARCH_TYPE_PROVIDERS]: {
    title: ["Find a", "Provider"],
    blurb:
      "Search therapists, coaches and holistic practitioners by name, specialty or location.",
  },
  [SEARCH_TYPE_ORGANIZATIONS]: {
    title: ["Find an", "Organization"],
    blurb:
      "Search group practices and organizations by name, specialty or location.",
  },
}

// this section renders headers, search bar and other top info on the home page and search results page.
function TopSection({
  searchTerm,
  // handleUpdateSearch,
  areQueryParamsPresent,
  isResultsMode = areQueryParamsPresent,
  type: typeProp,
  groupTypes,
  providerTypes,
  virtual,
  inPerson,
  isTherapyGroups,
  isSupportGroups,
  isRetreats,
  isWorkshops,
  isProviderGroups,
  isCoaches,
  isTherapists,
  isAlternative,
  latitude,
  longitude,
  location,
}) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const type: SearchType = parseSearchType(typeProp)
  const filters = SEARCH_FILTERS_BY_TYPE[type] ?? SEARCH_FILTERS

  // Every search change (term, location, filters, tab) lands on page 1 —
  // only SearchPagination links carry a page. Switching tabs keeps the term
  // and location but drops the chips, which belong to the tab they were set on.
  const handleUpdateSearch = (options: UpdateSearchOptions = {}) => {
    startTransition(() => {
      const {
        newSearchTerm = searchTerm,
        newLocation = location,
        newGroupTypes = groupTypes,
        newProviderTypes = providerTypes,
        newVirtual = virtual,
        newInPerson = inPerson,
        newLatitude = latitude,
        newLongitude = longitude,
        newType = type,
      } = options
      const tabChanged = newType !== type

      router.push(
        buildSearchHref({
          searchTerm: newSearchTerm,
          location: newLocation,
          groupTypes: tabChanged ? [] : newGroupTypes,
          providerTypes: tabChanged ? [] : newProviderTypes,
          virtual: newVirtual,
          inPerson: newInPerson,
          latitude: newLatitude,
          longitude: newLongitude,
          type: newType,
        }),
      )
    })
  }

  const renderSearchBar = () => {
    return (
      <SearchBar
        key="top-section-search-bar"
        searchType={type}
        initialSearchTerm={searchTerm}
        handleUpdateSearch={handleUpdateSearch}
        initalLatitude={latitude}
        initalLongitude={longitude}
        initialLocation={location}
      />
    )
  }

  const renderHeaderAndDescription = () => {
    const copy = HEADER_COPY[type] ?? HEADER_COPY[SEARCH_TYPE_GROUPS]
    return isResultsMode ? (
      <div className={"pt-[20px] min-[800px]:pt-[40px]"} key="nah-query" />
    ) : (
      <React.Fragment key="header-description-container">
        <h1
          className="font-bebas text-[36px] tracking-[2.52px] text-center pt-[40px] text-black flex justify-center flex-col m-[0px] min-[500px]:flex-row"
          key="disc"
        >
          <div className="min-[500px]:mr-[8px]">{copy.title[0]}</div>
          <div>{copy.title[1]}</div>
        </h1>
        <div
          className="pt-[19px] pb-[50px] flex justify-center items-center"
          key="search-stuff"
        >
          <div
            className={
              " text-center text-[20px] tracking-[1.4px] min-[600px]:w-[490px] min-[1125px]:w-auto"
            }
            key="search"
          >
            {copy.blurb}
          </div>
        </div>
      </React.Fragment>
    )
    /* Find your community for growth, led by independent professionals */
  }

  const renderBelowSearchBar = () => {
    if (type === SEARCH_TYPE_GROUPS && !isResultsMode) {
      return (
        <SuggestedSearchPills
          key="suggested-search-pills"
          handleUpdateSearch={handleUpdateSearch}
        />
      )
    }
    return <div className="h-[33px]" key={"height"} />
  }

  return [
    isPending ? <Loader key="top-section-loader" /> : null,
    <div
      className={
        "flex flex-col justify-center bg-blue-accent-2 text-white flex flex-col pt-[60px] px-[10px]"
      }
      key={"top-section"}
    >
      {renderHeaderAndDescription()}
      {renderSearchBar()}
      <SearchTypeToggle
        key="search-type-toggle"
        value={type}
        disabled={isPending}
        onChange={(newType) => handleUpdateSearch({ newType })}
      />
      {renderBelowSearchBar()}
    </div>,
    <SearchFilters
      key={"top-section-search-filters"}
      filters={filters}
      groupTypes={groupTypes}
      providerTypes={providerTypes}
      virtual={virtual}
      inPerson={inPerson}
      isTherapyGroups={isTherapyGroups}
      isSupportGroups={isSupportGroups}
      isRetreats={isRetreats}
      isWorkshops={isWorkshops}
      isProviderGroups={isProviderGroups}
      isCoaches={isCoaches}
      isTherapists={isTherapists}
      isAlternative={isAlternative}
      handleUpdateSearch={handleUpdateSearch}
      areQueryParamsPresent={areQueryParamsPresent}
      isPending={isPending}
    />,
    <div
      key={"top-section-spacer"}
      className={
        "w-full pb-[5px] border-b-black border-solid border-b-[1px] border-t-[0px] border-l-[0px] border-r-[0px]"
      }
    />,
  ]
}

export default TopSection
